import { Component, ElementRef, NgZone, OnDestroy, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as EXIF from 'exif-js';
import { Subscription } from 'rxjs';
import { ApiClientConstant } from '@cureskin/api-client';
import { ConnectionService } from '../../../services/connection-service';
import { WindowRefService } from '../../../services/window-ref-service';
import { AppConfig } from '../../app.config';
import { LocalStorage } from '../../../services/local-storage-service';
import { Broadcaster } from '../../../components/broadcaster';
import { FaviconService } from '../../../services/favicon-service';
import { TabNamingService } from '../../../services/tab-naming-service';

@Component({
    selector: 'instant-checkup-view',
    templateUrl: './view.html',
    styleUrls: ['./view.scss'],
    standalone: false
})
export class InstantCheckupViewComponent implements OnDestroy {
  @Input() hideFullImage: boolean;
  result: any;
  instantCheckupId: string;
  instantCheckupObject: any;
  instantCheckup: any;
  displayPercentage: any;
  detectedIssuesText: any;
  image: any;
  context: any;
  orientation: any;
  subscriptions: any;
  checkupType: string;
  userInInstantCheckupScore: boolean;
  showAiResponse: boolean = false;
  internalUser: any;
  issueMarkingContext: any;
  userRoles: Array<any> = [];
  private lineDashOffset: number = 0;
  private pulseAngle: number = 0;
  private pulseScale: number = 1;
  private animationFrameId: number | null = null;
  private currentProblem: any = null;
  private currentSliderValue: any;
  private static readonly SCALP_STROKE_COLOR: string = '#5CC7B6';
  private static readonly DEFAULT_STROKE_COLOR: string = 'rgb(237, 4, 0)';
  private static readonly DEFAULT_DASH_PATTERN: Array<number> = [5, 3, 5, 3];
  private static readonly LINE_DASH_OFFSET_INCREMENT: number = 0.35;
  private static readonly PULSE_ANGLE_INCREMENT: number = Math.PI / 150;
  private static readonly PULSE_SCALE_MIN: number = 0.9;
  private static readonly PULSE_SCALE_MAX: number = 1.2;
  private static readonly TEAL_CLASSES: Array<string> = ['scalp-segment', 'temple-baldness'];
  private static readonly SCALP_FILL_CLASS: string = 'scalp-segment';
  private static readonly SCALP_FILL_ALPHA: number = 0.44;
  @Input('instantCheckupId')
  set value(id: string) {
    this.instantCheckupId = id;
    if (this.user) this.fetchInstantCheckup();
  }
  @Input('username') username: any;
  user: any;
  @ViewChild('canvasIC', { static: false }) canvas: ElementRef;
  @ViewChild('canvasIssueMarking', { static: false }) canvasIssueMarking: ElementRef;
  @Output('deleted') deleted: EventEmitter<void> = new EventEmitter();
  ui: any = {};
  response: any;
  sliderValue: any = {};
  static parameters: any = [
    ConnectionService,
    Router,
    AppConfig,
    WindowRefService,
    ActivatedRoute,
    NgZone,
    LocalStorage,
    Broadcaster,
    FaviconService,
    TabNamingService,
  ];
  constructor(private conn: ConnectionService,
              private router: Router,
              public appConfig: AppConfig,
              private windowRef: WindowRefService,
              private route: ActivatedRoute,
              private zone: NgZone,
              private storage: LocalStorage,
              private broadcaster: Broadcaster,
              private faviconService: FaviconService,
              private tabNaming: TabNamingService) {
  }

  async ngOnInit(): Promise<any> {
    this.reset();
    this.route.queryParams.subscribe(async (params: any) => {
      this.user = await this.conn.getUserByUserName(this.username);
      if (this.conn.getCurrentUser().get('type')) this.internalUser = this.conn.getCurrentUser();
      this.fetchInstantCheckup();
      this.broadcaster.broadcast('ChatUserUpdate', { user: this.user });
      this.faviconService.setFavicon(this.user.get('PatientName'));
      this.tabNaming.setTitle('', this.user.get('PatientName'), 'Instant checkup');
    });
    this.userRoles = this.storage.getJsonValue('userRoles');
  }

  reset(): void {
    this.result = [];
    delete this.instantCheckup;
    delete this.instantCheckupObject;
    this.orientation = -1;
    this.subscriptions = [];
    this.detectedIssuesText = [];
    this.ui = { selectedCard: -1, imageLoaded: false, popUpModal: { open: false } };
  }

  async fetchInstantCheckup(): Promise<any> {
    this.reset();
    this.instantCheckup = (await this.conn.fetchUserInstantCheckup({ userId: this.user.get('username'), id: [this.instantCheckupId] }))[0];
    this.instantCheckupObject = await this.conn.getInstantCheckupById(this.instantCheckupId);
    this.checkupType = this.instantCheckup.type;
    if (this.instantCheckup.aiResponse && this.instantCheckup.aiResponse.imageHeight && this.instantCheckup.aiResponse.imageWidth) {
      this.instantCheckup.dimension = { width: this.instantCheckup.aiResponse.imageWidth,
        height: this.instantCheckup.aiResponse.imageHeight };
    }
    this.loadData();
  }

  loadData(): any {
    this.loadImageAndRenderIssues();
    this.userInInstantCheckupScore = true;
    return 0;
  }

  toggleAiResponse(): any {
    this.showAiResponse = !this.showAiResponse;
  }

  loadImageAndRenderIssues(): any {
    this.image = new Image();
    this.image.crossOrigin = 'Anonymous';
    this.image.onload = (): void => {
      this.afterImageLoad();
    };
    let base64ImageData;
    if (base64ImageData) {
      base64ImageData = `data:image/png;base64,${base64ImageData}`;
      this.image.src = base64ImageData;
    } else {
      this.image.src = this.instantCheckup.imagePath;
    }
    if (this.checkupType === ApiClientConstant.InstantCheckup.Type.HAIR_TOP) {
      this.response = JSON.stringify(this.instantCheckup.aiResponse);
      this.processScalpDetections();
      return 0;
    }
    if (!([
      ApiClientConstant.InstantCheckup.Type.FULL_FACE,
      ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
    ] as Array<string>).includes(this.checkupType)) return 0;
    let results = [];
    results.push({ ProblemName: 'All', Condition: 'Detected' });
    if (this.instantCheckup.aiResponse && this.instantCheckup.aiResponse.result) {
      results.push(...this.instantCheckup.aiResponse.result);
    } else if (this.instantCheckup.resultObject) {
      results.push(...JSON.parse(this.instantCheckup.resultObject));
    }
    this.response = JSON.stringify(this.instantCheckup.aiResponse);
    const tempResult = results.filter((each: any) => {
      if (each.showForGender && this.user.get('Gender').toLowerCase() === each.showForGender.toLowerCase()) return true;
      if (!each.showForGender) return true;
      return false;
    });
    results = tempResult;
    if (results.length > 1) {
      results.forEach((each: any) => {
        if (each.Condition !== 'Not Detected') this.result.push(each);
      });
    }
    return 0;
  }

  async afterImageLoad(): Promise<any> {
    const { src }: { src: string } = this.image;
    const rotationNeeded = this.conn.rotationNeeded();
    EXIF.getData(this.image, () => {
      const orientation = rotationNeeded ? EXIF.getTag(this.image, 'Orientation') : 0;
      this.orientation = this.instantCheckupObject.get('orientation')
        ? this.instantCheckupObject.get('orientation')
        : orientation;
      this.context = this.canvas.nativeElement.getContext('2d');
      this.issueMarkingContext = this.canvasIssueMarking.nativeElement.getContext('2d');
      this.canvas.nativeElement.width = this.windowRef.nativeWindow.innerWidth;
      if (this.instantCheckup.dimension) {
        this.canvasIssueMarking.nativeElement.width = this.instantCheckup.dimension.width;
        this.canvasIssueMarking.nativeElement.height = this.instantCheckup.dimension.height;
      } else {
        this.canvasIssueMarking.nativeElement.width = this.image.width;
        this.canvasIssueMarking.nativeElement.height = this.image.height;
      }
      if ([5, 6, 7, 8].includes(this.orientation)) {
        if (this.image.height <= this.windowRef.nativeWindow.innerWidth) {
          this.canvas.nativeElement.height = this.image.width * (this.windowRef.nativeWindow.innerWidth / this.image.height);
        } else {
          this.canvas.nativeElement.height = this.image.width / (this.image.height / this.windowRef.nativeWindow.innerWidth);
        }
        this.rotateCanvasBasedOnOrientation(this.orientation, this.context, this.canvas);
        this.context.drawImage(
          this.image,
          0,
          0,
          this.image.width,
          this.image.height,
          0,
          0,
          this.canvas.nativeElement.height,
          this.canvas.nativeElement.width);
      } else {
        if (this.image.width <= this.windowRef.nativeWindow.innerWidth) {
          this.canvas.nativeElement.height = this.image.height * (this.windowRef.nativeWindow.innerWidth / this.image.width);
        } else {
          this.canvas.nativeElement.height = this.image.height / (this.image.width / this.windowRef.nativeWindow.innerWidth);
        }
        this.rotateCanvasBasedOnOrientation(this.orientation, this.context, this.canvas);
        this.context.drawImage(
          this.image,
          0,
          0,
          this.image.width,
          this.image.height,
          0,
          0,
          this.canvas.nativeElement.width,
          this.canvas.nativeElement.height);
      }
      this.canvas.nativeElement.style.display = 'block';
      this.canvasIssueMarking.nativeElement.style.display = 'block';
      this.ui.imageLoaded = true;
      this.context.save();
      if (this.internalUser) {
        this.canvas.nativeElement.style.width = this.internalUser.get('instantCheckupWidth') || '35%';
        this.canvas.nativeElement.style.margin = 'auto';
        this.canvasIssueMarking.nativeElement.style.width = this.internalUser.get('instantCheckupWidth') || '35%';
        this.canvasIssueMarking.nativeElement.style.margin = 'auto';
      }
    });
  }

  rotateCanvasBasedOnOrientation(orientation: any, ctx: any, canvas: any): void {
    switch (orientation) {
      case 6: {
        ctx.translate(canvas.nativeElement.width, 0);
        ctx.rotate(90 * (Math.PI / 180));
        break;
      }
      case 8: {
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-canvas.nativeElement.height, 0);
        break;
      }
      case 3: {
        ctx.rotate(Math.PI);
        ctx.translate(-canvas.nativeElement.width, -canvas.nativeElement.height);
        break;
      }
      case 5: {
        ctx.transform(0, 1, 1, 0, 0, 0);
        break;
      }
      case 7: {
        ctx.transform(0, -1, -1, 0, canvas.nativeElement.width, canvas.nativeElement.height);
        break;
      }
      case 4: {
        ctx.transform(1, 0, 0, -1, 0, canvas.nativeElement.height);
        break;
      }
      case 2: {
        ctx.transform(-1, 0, 0, 1, canvas.nativeElement.width, 0);
        break;
      }
      default: break;
    }
  }

  drawProblem(problem: any, index: any, skipEventTracking?: boolean, sliderValue?: any): void {
    this.issueMarkingContext.clearRect(0, 0, this.canvasIssueMarking.nativeElement.width, this.canvasIssueMarking.nativeElement.height);
    if (this.ui.selectedCard === index && sliderValue === undefined) {
      this.ui.selectedCard = -1;
      this.currentProblem = null;
      this.stopAnimation();
      return;
    }
    this.ui.selectedCard = index;
    this.currentProblem = problem;
    this.currentSliderValue = sliderValue;
    this.markIssues(problem, sliderValue);
    this.startAnimation();
  }

  isScalpSelected(): boolean {
    return InstantCheckupViewComponent.TEAL_CLASSES.includes(this.currentProblem?.class);
  }

  private startAnimation(): void {
    if (this.animationFrameId !== null) return;
    const tick = (): void => {
      this.lineDashOffset = (this.lineDashOffset + InstantCheckupViewComponent.LINE_DASH_OFFSET_INCREMENT) % 1000;
      this.pulseAngle += InstantCheckupViewComponent.PULSE_ANGLE_INCREMENT;
      this.pulseScale = InstantCheckupViewComponent.PULSE_SCALE_MIN
        + Math.abs(Math.sin(this.pulseAngle))
          * (InstantCheckupViewComponent.PULSE_SCALE_MAX - InstantCheckupViewComponent.PULSE_SCALE_MIN);
      if (this.currentProblem && this.issueMarkingContext && this.canvasIssueMarking) {
        this.issueMarkingContext.clearRect(
          0,
          0,
          this.canvasIssueMarking.nativeElement.width,
          this.canvasIssueMarking.nativeElement.height,
        );
        this.markIssues(this.currentProblem, this.currentSliderValue);
      }
      this.animationFrameId = requestAnimationFrame(tick);
    };
    this.animationFrameId = requestAnimationFrame(tick);
  }

  private stopAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  markIssues(problem: any, sliderValue?: any): void {
    const container = [];
    if (problem.ProblemName === 'All') {
      container.push(...this.result);
      container.shift();
    } else container.push(problem);
    container.forEach((each: any) => {
      const item = each;
      if (!each.showForGender || this.user.get('Gender') === each.showForGender) {
        if (item.BoundingBoxes || item.BoundingBoxesActual) this.drawIssuesInCircle(item, sliderValue);
        if (item.PolygonPointsSequences || item.ellipse_points || item.hairline_points) this.drawIssuesInPolygon(item);
      }
    });
  }

  private getDetectionScale(): number {
    const attrWidth = this.canvasIssueMarking?.nativeElement?.width;
    const displayWidth = this.canvas?.nativeElement?.clientWidth || this.canvas?.nativeElement?.width;
    if (!attrWidth || !displayWidth) return 1;
    return attrWidth / displayWidth;
  }

  drawIssuesInCircle(item: any, sliderValue?: any): void {
    const arr = Number(sliderValue) >= 0
      ? (item.BoundingBoxesActual || item.BoundingBoxes)
      : (item.BoundingBoxes || item.BoundingBoxesActual);
    if (!arr) return;
    const scale = this.getDetectionScale();
    arr.forEach((each: any, index: any) => {
      if (!each) return;
      const point = each.split(',').map((x: string): number => +x);
      const centerX = (point[0] + point[2]) / 2;
      const centerY = (point[1] + point[3]) / 2;
      const radius = (Math.abs(point[0] - point[2]) / 2) * this.pulseScale;
      if (!(Number(sliderValue) >= 0) || (item.confidencesActual && Number(item.confidencesActual[index]) >= (sliderValue / 10))) {
        this.issueMarkingContext.beginPath();
        this.issueMarkingContext.setLineDash([]);
        this.issueMarkingContext.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        this.issueMarkingContext.lineWidth = 2 * scale;
        this.issueMarkingContext.strokeStyle = 'rgba(237, 4, 0)';
        this.issueMarkingContext.stroke();
        this.issueMarkingContext.closePath();
      }
    });
  }

  drawIssuesInPolygon(item: any): void {
    let polygons: Array<any> = [];
    if (item.PolygonPointsSequences) {
      polygons = item.PolygonPointsSequences;
    } else if (item.ellipse_points) {
      polygons = [[...(item.ellipse_points.left || [])], [...(item.ellipse_points.right || [])]];
    } else if (item.hairline_points) {
      polygons = [{ points: [...item.hairline_points], color: '#ffffff' }];
    }
    const scale = this.getDetectionScale();
    const defaultLineWidth = 1.5 * scale;
    const dash = InstantCheckupViewComponent.DEFAULT_DASH_PATTERN.map((d: number): number => d * scale);
    const useTeal = InstantCheckupViewComponent.TEAL_CLASSES.includes(item.class);
    const defaultColor = useTeal
      ? InstantCheckupViewComponent.SCALP_STROKE_COLOR
      : InstantCheckupViewComponent.DEFAULT_STROKE_COLOR;
    const shouldFill = item.class === InstantCheckupViewComponent.SCALP_FILL_CLASS;

    polygons.forEach((polygon: any) => {
      const isColored = polygon && !Array.isArray(polygon) && 'color' in polygon;
      const points = isColored ? polygon.points : polygon;
      const strokeStyle = isColored ? polygon.color : defaultColor;
      this.drawPolygon(points, defaultLineWidth, dash, strokeStyle, scale, shouldFill ? strokeStyle : null);
    });
  }

  private drawPolygon(
    points: any[],
    lineWidth: number,
    lineDash: number[],
    strokeStyle: string,
    scale: number = 1,
    fillStyle: string | null = null,
  ): void {
    if (!points?.length) return;
    this.issueMarkingContext.beginPath();
    this.issueMarkingContext.setLineDash(lineDash);
    this.issueMarkingContext.lineDashOffset = this.lineDashOffset * scale;
    points.forEach((cords: any, index: number) => {
      if (!cords) return;
      let pointX: number;
      let pointY: number;
      if (cords instanceof Array) {
        [pointX, pointY] = cords;
      } else {
        [pointX, pointY] = String(cords).split(',').map(Number);
      }
      if (index === 0) {
        this.issueMarkingContext.moveTo(pointX, pointY);
      } else {
        this.issueMarkingContext.lineTo(pointX, pointY);
      }
    });
    if (fillStyle) {
      this.issueMarkingContext.save();
      this.issueMarkingContext.globalAlpha = InstantCheckupViewComponent.SCALP_FILL_ALPHA;
      this.issueMarkingContext.fillStyle = fillStyle;
      this.issueMarkingContext.fill();
      this.issueMarkingContext.restore();
    }
    this.issueMarkingContext.lineWidth = lineWidth;
    this.issueMarkingContext.lineJoin = 'round';
    this.issueMarkingContext.lineCap = 'round';
    this.issueMarkingContext.strokeStyle = strokeStyle;
    this.issueMarkingContext.stroke();
    this.issueMarkingContext.closePath();
  }

  private processScalpDetections(): void {
    const results = [...(this.instantCheckup?.aiResponse?.result || [])];
    const scalpDetections = results.filter((result: any): boolean =>
      result.Condition === 'Detected' && result.class === 'scalp-segment'
    );
    if (scalpDetections.length === 0) return;
    this.result.push({ ProblemName: 'All', Condition: 'Detected' });
    scalpDetections.forEach((detection: any): void => {
      try {
        const scalpMasks = JSON.parse(detection.scalp_masks || '[]');
        if (!Array.isArray(scalpMasks) || scalpMasks.length === 0) return;
        this.result.push({ ...detection, PolygonPointsSequences: scalpMasks });
      } catch (error) {
        console.error('Error parsing scalp_masks:', error);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopAnimation();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  async saveSliderValue(item: any): Promise<any> {
    const score = this.instantCheckupObject.get('thresholdByDoctors') || {};
    score[item.ProblemName.split(' ').join('_').toUpperCase()] = this.sliderValue[item.ProblemName] / 10;
    this.instantCheckupObject.set('thresholdByDoctors', score);
    await this.instantCheckupObject.save();
    this.showToastMessage('Saved');
  }

  sliderChange(item: any, index: any, flag: boolean): void {
    if (!item.confidencesActual) return;
    this.drawProblem(item, index, flag, this.sliderValue[item.ProblemName]);
  }

  changeDisplay(save: boolean = false): any {
    if (this.internalUser) {
      this.canvas.nativeElement.style.width = `${this.displayPercentage}%`;
      this.canvas.nativeElement.style.margin = 'auto';
      if (save) {
        this.internalUser.set('instantCheckupWidth', `${this.displayPercentage}%`);
        this.internalUser.save();
        this.showToastMessage('Saved width');
        this.displayPercentage = null;
      }
    }
  }

  showToastMessage(message: string): void {
    this.broadcaster.broadcast('NOTIFY', { message, type: this.appConfig.Shared.Toast.Type.SUCCESS });
  }
}
